import * as Sentry from '@sentry/react';
import { redactPublicTokensDeep } from '@repo/utils';
import { runtimeConfig } from '@/lib/runtime-config';

declare const __APP_VERSION__: string;

export interface SentryInitOptions {
  /**
   * Pagina pubblica col token nel path (link del rapportino, firma del
   * contratto): niente registrazione di sessione, che riprenderebbe documento
   * e indirizzo.
   */
  publicTokenPage: boolean;
}

/**
 * Hook che tolgono il token dei link pubblici da ogni testo inviato a Sentry:
 * URL della pageload e delle richieste (breadcrumb `xhr`/`fetch`/
 * `navigation`), nome della transazione, span, messaggi. Valgono su ogni
 * pagina: anche l'app autenticata può avere in mano un link con token.
 */
export const SENTRY_PUBLIC_TOKEN_SCRUBBING = {
  beforeSend: <T>(event: T): T => redactPublicTokensDeep(event),
  beforeSendTransaction: <T>(event: T): T => redactPublicTokensDeep(event),
  beforeBreadcrumb: <T>(breadcrumb: T): T => redactPublicTokensDeep(breadcrumb),
};

/**
 * La replay riprende saldi, importi, IBAN e anagrafiche: testi mascherati e
 * media bloccati, sempre.
 */
export const SENTRY_REPLAY_PRIVACY = {
  maskAllText: true,
  blockAllMedia: true,
} as const;

export function buildSentryIntegrations({
  publicTokenPage,
}: SentryInitOptions) {
  return [
    Sentry.browserTracingIntegration(),
    ...(publicTokenPage
      ? []
      : [Sentry.replayIntegration(SENTRY_REPLAY_PRIVACY)]),
  ];
}

export function initSentry(options: SentryInitOptions) {
  const dsn = runtimeConfig.VITE_SENTRY_DSN_WEB;
  if (!dsn) return;

  Sentry.init({
    dsn,
    environment: runtimeConfig.VITE_APP_ENV || import.meta.env.MODE,
    release: __APP_VERSION__,
    integrations: buildSentryIntegrations(options),
    tracesSampleRate:
      (runtimeConfig.VITE_APP_ENV || import.meta.env.MODE) === 'production'
        ? 0.2
        : 1.0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: options.publicTokenPage ? 0 : 1.0,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      /Loading chunk .* failed/,
    ],
    denyUrls: [/extensions\//i, /^chrome:\/\//i],
    ...SENTRY_PUBLIC_TOKEN_SCRUBBING,
  });

  Sentry.setTag('app', 'web');
}
